import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

/* ── Icon helpers ── */
const WebIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><line x1="2" x2="22" y1="12" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);
const PhoneIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.19 12 19.79 19.79 0 0 1 1.1 3.33A2 2 0 0 1 3.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);
const MapIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);
const ArrowIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
  </svg>
);
const ShieldIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

/* ── Animation variants ── */
const containerVariants = {
  hidden:  { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.13, delayChildren: 0.2 } },
};
const itemVariants = {
  hidden:  { y: 28, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.6, ease: 'easeOut' } },
};

/* ── Component ── */
export function HeroSection({
  logo,
  slogan,
  title,
  subtitle,
  callToAction,
  secondaryAction,
  backgroundImage,
  contactInfo,
}) {
  const { scrollY } = useScroll();
  const imageY = useTransform(scrollY, [0, 800], ['0%', '12%']);

  return (
    <motion.section
      className="relative flex w-full flex-col overflow-hidden md:flex-row"
      style={{ minHeight: '100vh', backgroundColor: '#02AEF2' }}
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* ── LEFT PANEL ── */}
      <div
        className="relative flex w-full flex-col justify-between px-5 sm:px-8 md:px-10 lg:px-14 pb-10 md:w-[58%] lg:w-[55%]"
        style={{ paddingTop: 'clamp(5.5rem, 9vw, 8.5rem)' }}
      >
        {/* Dot-grid background texture */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{ backgroundImage: 'radial-gradient(circle, #000 1px, transparent 0)', backgroundSize: '28px 28px' }}
        />

        {/* Animated blobs */}
        <div className="pointer-events-none absolute -top-28 -left-28 w-96 h-96 rounded-full bg-white/10 blur-3xl animate-blob" />
        <div
          className="pointer-events-none absolute top-1/2 right-8 w-72 h-72 rounded-full bg-brand-red/6 blur-3xl animate-blob"
          style={{ animationDelay: '2.6s' }}
        />
        <div
          className="pointer-events-none absolute -bottom-12 left-1/4 w-80 h-80 rounded-full bg-white/5 blur-3xl animate-blob"
          style={{ animationDelay: '4.5s' }}
        />

        <div className="relative z-10">
          {/* Logo */}
          <motion.header className="mb-8 md:mb-11" variants={itemVariants}>
            {logo && (
              <div className="flex items-center gap-3">
                <img
                  src={logo.url}
                  alt={logo.alt}
                  className="h-14 w-14 rounded-full object-contain ring-2 ring-black/12 shadow-md bg-black/5"
                />
                <div>
                  {logo.text && (
                    <p className="text-[13px] font-bold text-gray-900 font-poppins tracking-widest uppercase leading-tight">
                      {logo.text}
                    </p>
                  )}
                  {slogan && (
                    <p className="text-[10px] tracking-[0.3em] text-gray-600 uppercase font-inter mt-0.5 font-bold">
                      {slogan}
                    </p>
                  )}
                </div>
              </div>
            )}
          </motion.header>

          {/* Animated badge */}
          <motion.div className="mb-5" variants={itemVariants}>
            <span
              className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[11px] font-semibold tracking-wider text-gray-800 uppercase font-inter border"
              style={{
                background:           'rgba(0,0,0,0.07)',
                backdropFilter:       'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                borderColor:          'rgba(0,0,0,0.08)',
              }}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-brand-red animate-pulse" />
              Kenya's Trusted Facilities Partner
            </span>
          </motion.div>

          <motion.h1
            className="font-black font-poppins text-gray-900 leading-[1.06] tracking-tight"
            style={{ fontSize: 'clamp(2.1rem, 4.5vw, 3.75rem)' }}
            variants={itemVariants}
          >
            {title}
          </motion.h1>

          {/* Brand divider */}
          <motion.div className="flex items-center gap-1.5 my-5 sm:my-6" variants={itemVariants}>
            <div className="h-[3px] w-14 rounded-full bg-brand-red" />
            <div className="h-[3px] w-4 rounded-full bg-black/15" />
            <div className="h-[3px] w-4 rounded-full bg-brand-red" />
          </motion.div>

          <motion.p
            className="mb-7 max-w-[480px] text-[15px] leading-relaxed text-gray-700 font-inter font-medium"
            variants={itemVariants}
          >
            {subtitle}
          </motion.p>

          {/* CTAs */}
          <motion.div className="flex flex-wrap gap-3 mb-8" variants={itemVariants}>
            <a
              href={callToAction.href}
              className="group inline-flex items-center gap-2.5 rounded-full bg-brand-red px-7 py-3.5 text-sm font-bold text-white font-poppins shadow-lg shadow-brand-red/25 transition-all duration-250 hover:bg-brand-red-dark hover:-translate-y-1 hover:shadow-xl hover:shadow-brand-red/35 cursor-pointer"
            >
              {callToAction.text}
              <span className="group-hover:translate-x-1 transition-transform duration-200">
                <ArrowIcon />
              </span>
            </a>
            {secondaryAction && (
              <a
                href={secondaryAction.href}
                className="inline-flex items-center gap-2 rounded-full border-2 border-black/12 px-6 py-3.5 text-sm font-bold text-gray-900 font-poppins transition-all duration-250 hover:bg-black/10 hover:-translate-y-1 cursor-pointer"
                style={{ background: 'rgba(0,0,0,0.07)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
              >
                <PhoneIcon />
                {secondaryAction.text}
              </a>
            )}
          </motion.div>

          {/* Quick stats */}
          <motion.div className="flex flex-wrap gap-5 sm:gap-8" variants={itemVariants}>
            {[
              { val: '500+', label: 'Happy Clients' },
              { val: '15+',  label: 'Years Experience' },
              { val: '6',    label: 'Service Lines' },
            ].map(({ val, label }) => (
              <div key={label} className="flex items-center gap-2.5">
                <span className="text-2xl font-black text-gray-900 font-poppins leading-none">{val}</span>
                <span className="text-[11px] text-gray-600 font-inter leading-tight max-w-[3.5rem]">{label}</span>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Glass contact strip */}
        {contactInfo && (
          <motion.footer className="relative z-10 mt-10 sm:mt-12" variants={itemVariants}>
            <div
              className="rounded-xl p-3 sm:p-4 flex flex-wrap gap-x-5 gap-y-2"
              style={{
                background:           'rgba(0,0,0,0.06)',
                backdropFilter:       'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border:               '1px solid rgba(0,0,0,0.06)',
              }}
            >
              {contactInfo.website && (
                <div className="flex items-center gap-2 text-[11px] text-gray-700 font-inter font-bold">
                  <span className="text-brand-red"><WebIcon /></span>
                  <span>{contactInfo.website}</span>
                </div>
              )}
              {contactInfo.phone && (
                <a
                  href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
                  className="flex items-center gap-2 text-[11px] text-gray-700 hover:text-gray-900 transition-colors font-inter font-bold"
                >
                  <span className="text-brand-red"><PhoneIcon /></span>
                  <span>{contactInfo.phone}</span>
                </a>
              )}
              {contactInfo.address && (
                <div className="flex items-center gap-2 text-[11px] text-gray-700 font-inter font-bold">
                  <span className="text-brand-red"><MapIcon /></span>
                  <span>{contactInfo.address}</span>
                </div>
              )}
            </div>
          </motion.footer>
        )}
      </div>

      {/* ── RIGHT PANEL — hidden on mobile, diagonal reveal on desktop ── */}
      <motion.div
        className="hidden md:block relative md:flex-1 overflow-hidden"
        initial={{ clipPath: 'polygon(100% 0, 100% 0, 100% 100%, 100% 100%)' }}
        animate={{ clipPath: 'polygon(16% 0, 100% 0, 100% 100%, 0% 100%)' }}
        transition={{ duration: 1.3, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
      >
        {/* Image */}
        <motion.div
          className="absolute inset-0 bg-cover bg-center scale-110 origin-center"
          style={{ backgroundImage: `url(${backgroundImage})`, y: imageY }}
        />

        {/* Gradient overlay — stronger on mobile for text legibility */}
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to right, rgba(2,174,242,0.55) 0%, rgba(0,48,135,0.18) 50%, transparent 100%)',
          }}
        />

        {/* NEMA glass badge — shown on mobile too (smaller) */}
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.88 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 1.2, duration: 0.55, ease: 'easeOut' }}
          className="absolute bottom-5 right-4 flex items-center gap-2 sm:gap-3 rounded-xl sm:rounded-2xl p-2.5 sm:p-4 cursor-default"
          style={{
            background:           'rgba(255,255,255,0.12)',
            backdropFilter:       'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
            border:               '1px solid rgba(255,255,255,0.20)',
          }}
        >
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-brand-red rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
            <ShieldIcon />
          </div>
          <div>
            <p className="text-white font-bold font-poppins text-xs sm:text-sm leading-tight">NEMA Certified</p>
            <p className="text-white/70 text-[10px] sm:text-[11px] font-inter mt-0.5">Licensed &amp; Compliant</p>
          </div>
        </motion.div>

        {/* 24/7 availability badge */}
        <motion.div
          initial={{ opacity: 0, y: -20, scale: 0.88 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 1.5, duration: 0.55, ease: 'easeOut' }}
          className="absolute top-5 right-4 flex items-center gap-2 rounded-xl px-2.5 sm:px-3 py-1.5 sm:py-2 cursor-default"
          style={{
            background:           'rgba(227,6,19,0.85)',
            backdropFilter:       'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border:               '1px solid rgba(255,255,255,0.15)',
          }}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
          <span className="text-white text-[10px] sm:text-[11px] font-bold font-inter tracking-wide">24/7 EMERGENCY</span>
        </motion.div>
      </motion.div>
    </motion.section>
  );
}
